/**
 * Converters between a coverage's nested `CoverageOption[]` (option → sub-option) and the flat
 * `ConfiguratorRow[]` the options table renders/mutates.
 */
import { CoverageOption, CoverageSubOption, deriveOptionCategories, sortCategories } from './coverage-option.model';
import { ConfiguratorRow, ConfiguratorRowType } from './package-configurator.model';

/** Nested options → flat rows (option row followed by its sub-option rows). */
export function flattenOptions(options: CoverageOption[]): ConfiguratorRow[] {
  const rows: ConfiguratorRow[] = [];
  for (const option of options) {
    rows.push(optionToRow(option));
    for (const sub of option.subOptions) rows.push(subOptionToRow(sub, option.id));
  }
  return rows;
}

/** Flat rows → nested options (sub-option rows fold into their parent option). */
export function toCoverageOptions(rows: ConfiguratorRow[]): CoverageOption[] {
  const options: CoverageOption[] = [];
  const optionsById = new Map<string, CoverageOption>();

  for (const row of rows) {
    if (row.rowType === ConfiguratorRowType.Option) {
      const option = rowToOption(row);
      optionsById.set(row.id, option);
      options.push(option);
    } else if (row.parentId) {
      const parent = optionsById.get(row.parentId);
      if (parent) parent.subOptions.push(rowToSubOption(row));
    }
  }

  return options;
}

function optionToRow(option: CoverageOption): ConfiguratorRow {
  return {
    id: option.id,
    groupName: option.groupName,
    rowType: ConfiguratorRowType.Option,
    parentId: null,
    name: option.name,
    description: option.description,
    categories: deriveOptionCategories(option),
    limits: option.limits,
    clinicClasses: option.clinicClasses,
    diagnoses: option.diagnoses ?? [],
  };
}

function subOptionToRow(sub: CoverageSubOption, parentId: string): ConfiguratorRow {
  return {
    id: sub.id,
    groupName: sub.groupName,
    rowType: ConfiguratorRowType.SubOption,
    parentId,
    name: sub.name,
    description: sub.description,
    categories: sortCategories(sub.categories ?? []),
    limits: sub.limits,
    clinicClasses: [],
    diagnoses: [],
  };
}

function rowToOption(row: ConfiguratorRow): CoverageOption {
  return {
    id: row.id,
    groupName: row.groupName,
    name: row.name,
    description: row.description,
    categories: row.categories,
    limits: row.limits,
    clinicClasses: row.clinicClasses,
    diagnoses: row.diagnoses ?? [],
    subOptions: [],
  };
}

function rowToSubOption(row: ConfiguratorRow): CoverageSubOption {
  return {
    id: row.id,
    groupName: row.groupName,
    name: row.name,
    description: row.description,
    categories: row.categories,
    limits: row.limits,
  };
}
